import { InputType, PopupCloseSetting } from '@/constants'
import { useSessionStore, useUIStore } from '@/stores'
import { authService } from '@/services/authService'
import { getSessionStepInfo } from '@/utils/session'
import { isPortalStep } from './portalActions'
import {
  findPortalInput,
  inputTypeForPath,
  isItemScopedPath,
  itemIdFromPath,
  parentStepType,
  portalInputParams,
} from './portalNav'
import { returnFunnel } from './portalSession'

export type CloseAction = { kind: 'recover' } | { kind: 'submit'; input: any }

export function resolveCloseAction(step: any): CloseAction {
  const inputs = (step?.valid_inputs as any[]) || []
  const setting = Number(step?.popup_close_setting)
  if (!inputs.length || setting === PopupCloseSetting.NoClose) {
    return { kind: 'recover' }
  }
  const first = { kind: 'submit', input: inputs[0] } as CloseAction
  const last = { kind: 'submit', input: inputs[inputs.length - 1] } as CloseAction

  switch (setting) {
    case PopupCloseSetting.FirstInput:
      return first
    case PopupCloseSetting.LastInput:
      return last
    case PopupCloseSetting.UndoOrFirstInput:
      return step?.can_undo ? { kind: 'recover' } : first
    case PopupCloseSetting.UndoOrLastInput:
      return step?.can_undo ? { kind: 'recover' } : last
    default:
      return { kind: 'recover' }
  }
}

export const NAV_FAILED = 'We could not open that page. Please try again.'

// Router loaders must pass their pending pathname — window.location is still the old page.
export function isSyncedToUrl(path: string = window.location.pathname): boolean {
  const s = useSessionStore.getState()
  if (s.popupSession || !isPortalStep(s.session)) {
    return false
  }
  const target = inputTypeForPath(path)
  if (target === undefined) {
    return true
  }
  const session = s.session as any
  if (target !== Number(session?.step_type)) {
    return false
  }
  return !isItemScopedPath(path) || String(session?.selected_item?.id) === String(itemIdFromPath(path))
}

const JUMPABLE_STEP_TYPES: ReadonlySet<number> = new Set([
  InputType.CustomerPortalOrders,
  InputType.CustomerPortalSubscriptions,
  InputType.CustomerPortalPersonalInfo,
])

async function landOnStep(sessionId: number, type: number): Promise<any> {
  if (type === InputType.CustomerPortalHome) {
    return authService.home(sessionId)
  }
  if (!JUMPABLE_STEP_TYPES.has(type)) {
    throw new Error(NAV_FAILED)
  }
  return authService.jump(sessionId, { step_type: type })
}

export async function moveSessionToPath(path: string, isLatest: () => boolean = () => true): Promise<void> {
  if (isSyncedToUrl(path)) {
    return
  }
  const sessionId = (useSessionStore.getState().session as any)?.id
  const targetType = inputTypeForPath(path)
  if (!sessionId || targetType === undefined) {
    throw new Error(NAV_FAILED)
  }

  const parentType = parentStepType(targetType)

  const live = useSessionStore.getState()
  let input =
    parentType !== undefined && !live.popupSession && isPortalStep(live.session)
      ? findPortalInput(live.session, targetType)
      : undefined

  if (!input) {
    const jumped = await landOnStep(sessionId, parentType ?? targetType)
    const store = useSessionStore.getState()
    store.setSession(jumped)
    store.clearStepStack()
    if (parentType === undefined || !isLatest()) {
      return
    }
    input = findPortalInput(jumped, targetType)
    if (!input) {
      throw new Error(NAV_FAILED)
    }
  }

  const result = await authService.input(sessionId, portalInputParams(input.id, path))
  const store = useSessionStore.getState()
  store.setSession(result)
  if (result?.can_undo) {
    store.addToStepStack(getSessionStepInfo(result))
  }
}

interface InFlightResync {
  pathname: string
  promise: Promise<boolean>
}

let inFlight: InFlightResync | null = null

export function resyncPortalSession(
  pathname: string = window.location.pathname,
  { showLoader = false }: { showLoader?: boolean } = {}
): Promise<boolean> {
  if (inFlight?.pathname === pathname) {
    return inFlight.promise
  }
  const previous = inFlight?.promise
  const entry = { pathname } as InFlightResync
  entry.promise = (async () => {
    try {
      await previous
    } catch (error) {
      console.warn('Previous portal resync failed', error)
    }
    try {
      return await walkBackToPortal(pathname, showLoader)
    } finally {
      if (inFlight === entry) {
        inFlight = null
      }
    }
  })()
  inFlight = entry
  return entry.promise
}

async function walkBackToPortal(pathname: string, showLoader: boolean): Promise<boolean> {
  const recovered = await attemptRecovery(pathname, showLoader)
  // The single release point. On failure the pin stays: it is the only record of the live
  // funnel step, and clearing it makes every later resync early-return "already synced".
  if (recovered) {
    returnFunnel()
  }
  return recovered
}

async function attemptRecovery(pathname: string, showLoader: boolean): Promise<boolean> {
  if (isSyncedToUrl(pathname)) {
    return true
  }
  if (!(useSessionStore.getState().session as any)?.id) {
    return false
  }

  const ui = useUIStore.getState()
  if (showLoader) {
    ui.setForceDisplayLoader(true)
    ui.setLoading(true)
  }
  useSessionStore.getState().setPortalResyncing(true)

  try {
    await moveSessionToPath(pathname)
    return true
  } catch {
    return false
  } finally {
    useSessionStore.getState().setPortalResyncing(false)
    if (showLoader) {
      ui.setForceDisplayLoader(false)
      ui.setLoading(false)
    }
  }
}
